// FieldRenderer.jsx
import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styles from '../../styles/FieldRenderer.module.scss';
import MediaPicker from './MediaPicker';
import {
  selectItemById,
  selectImages,
  selectAudios,
  selectVideos,
} from '../../utils/projectSelectors';
import MediaCard from './MediaCard';

// type OptionMediaValue = {
//   source: 'option';
//   id: string;        // opt.name
//   name: string;      // opt.name
//   preview: string;   // opt.preview
//   url: string;       // same as preview, convenience for MediaCard
// };
// type ProjectMediaValue = {
//   source: 'project';
//   id: string;   // project item id
//   name: string; // filename
//   key: string;  // S3 key
// };
// type FormValue =
//   | string            // text / number
//   | OptionMediaValue  // media with options
//   | ProjectMediaValue // media from project
//   | null
//   | '';
// type Values = {
//   [cat: string]: {
//     [fname: string]: FormValue;
//   };
// };

// ---------- Type guards ----------
const isProjectRef = (v) => v && typeof v === 'object' && v.source === 'project';
const isOptionRef = (v) => v && typeof v === 'object' && v.source === 'option';

// ---------- Small leaf components ----------
function SelectField({ spec, value, onChange, disabled }) {
  const opts = useMemo(() => {
    return (spec.options || []).map((o) =>
      typeof o === 'string'
        ? { label: o, val: o }
        : { label: o.name ?? String(o.value ?? ''), val: String(o.value ?? o.name ?? '') },
    );
  }, [spec.options]);

  return (
    <div className={styles.fieldWrap}>
      <select
        className={styles.input}
        disabled={disabled}
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
      >
        {opts.map((o) => (
          <option key={o.label + o.val} value={o.val}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function TextField({ spec, value, onChange, disabled, inputType }) {
  const max = spec?.max_length ?? null;
  const str = value == null ? '' : String(value);
  const len = str.length;

  const common = {
    className: styles.input,
    value: str,
    onChange: (e) => onChange(e.target.value),
    placeholder: spec?.placeholder || '',
    maxLength: max ?? undefined,
    disabled,
  };

  return (
    <div className={styles.fieldWrap}>
      <input {...common} type="text" inputMode={inputType === 'number' ? 'numeric' : undefined} />
      {max != null && (
        <div className={styles.metaRow}>
          <span className={styles.charCounter}>
            {len}/{max}
          </span>
        </div>
      )}
    </div>
  );
}

// ---------- OptionMediaField (no validation) ----------
function OptionMediaField({ type, spec, value, onChange, disabled }) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const optionItems = useMemo(() => {
    return (spec.options || []).map((opt) => ({
      ...opt,
      url: opt.preview,
      id: opt.name,
    }));
  }, [spec.options]);

  const selectedOption = isOptionRef(value) ? value : null;
  const name = selectedOption?.name;
  const label = type === 'image' ? 'image' : type === 'audio' ? 'audio' : 'video';

  return (
    <div className={styles.fieldWrap}>
      {selectedOption ? (
        <MediaCard
          type={type}
          url={selectedOption.preview}
          name={name}
          onPickerOpen={() => setPickerOpen(true)}
          canSelect={false}
          viewOnly={disabled}
        />
      ) : (
        <div className={styles.emptyPickerRow}>
          <div className={styles.emptyHint}>No {label} selected</div>
          {!disabled && (
            <button type="button" className="shake__btnPrimary" onClick={() => setPickerOpen(true)}>
              Select
            </button>
          )}
        </div>
      )}

      {pickerOpen && (
        <MediaPicker
          type={type}
          onClose={() => setPickerOpen(false)}
          items={optionItems}
          onSelect={(item) => {
            onChange({ ...item, source: 'option' });
            setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
}

// ---------- ProjectMediaField (no validation) ----------
function ProjectMediaField({ type, value, onChange, disabled }) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const projectItems = useSelector(
    type === 'image' ? selectImages : type === 'audio' ? selectAudios : selectVideos,
  );

  // Backend does not store full project media ref, try to find it by id
  let media;

  if (isProjectRef(value)) {
    // Most cases we have the id
    if (value.id) {
      media = projectItems.find((it) => it.id === value.id);
    }
    // When we open created job, we won't have the id, try name match
    if (value.name && !media) {
      media = projectItems.find((it) => it.name === value.name);
    }
  }

  useEffect(() => {
    if (disabled) return; // don't mutate state in view-only mode
    if (!media) return;
    if (!isProjectRef(value)) return;

    // if value already has everything needed, do nothing
    if (value.id && value.key) return;
    onChange({
      source: 'project',
      id: media.id,
      name: media.name,
      key: media.key,
      location: media.location,
      // url is optional; keep if you want (FieldRenderer uses signedUrl for display anyway)
      url: media.signedUrl,
    });
  }, [
    disabled,
    media?.id,
    media?.key,
    onChange,
    // only re-run when the "missing-ness" changes
    isProjectRef(value) ? value?.id : undefined,
    isProjectRef(value) ? value?.key : undefined,
  ]);

  return (
    <div className={styles.fieldWrap}>
      {!!media ? (
        <MediaCard
          type={type}
          url={media.signedUrl}
          name={media.name}
          onPickerOpen={() => setPickerOpen(true)}
          canSelect={false}
          viewOnly={disabled}
        />
      ) : (
        <div className={styles.emptyPickerRow}>
          <div className={styles.emptyHint}>No {type} selected</div>
          {!disabled && (
            <button type="button" className="shake__btnPrimary" onClick={() => setPickerOpen(true)}>
              Select
            </button>
          )}
        </div>
      )}

      {pickerOpen && (
        <MediaPicker
          type={type}
          onClose={() => setPickerOpen(false)}
          items={projectItems}
          onSelect={(item) => {
            onChange({
              source: 'project',
              id: item.id,
              name: item.name,
              url: item.signedUrl || item.url,
              location: item.location,
              key: item.key,
            });
            setPickerOpen(false);
          }}
          onClear={() => {
            onChange(null);
            setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
}

// ---------- MediaField (router between Option/Project) ----------
function MediaField({ spec, value, onChange, disabled }) {
  const type = spec?.type;
  const hasOptions = Array.isArray(spec?.options) && spec.options.length > 0;

  if (hasOptions) {
    return (
      <OptionMediaField
        type={type}
        spec={spec}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }

  return <ProjectMediaField type={type} value={value} onChange={onChange} disabled={disabled} />;
}

// ---------- Public renderer ----------
export default function FieldRenderer({ spec, value, onChange, disabled = false }) {
  const type = spec?.type || 'text';
  const hasOptions = Array.isArray(spec?.options) && spec.options.length > 0;

  if (type === 'text' || type === 'number') {
    if (hasOptions) {
      return <SelectField spec={spec} value={value} onChange={onChange} disabled={disabled} />;
    }
    return (
      <TextField
        spec={spec}
        value={value}
        onChange={onChange}
        disabled={disabled}
        inputType={type}
      />
    );
  }

  if (['image', 'audio', 'video'].includes(type)) {
    return <MediaField spec={spec} value={value} onChange={onChange} disabled={disabled} />;
  }

  return null;
}
